import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { redirect } from "react-router-dom";

import { Spinner } from "@/components/ui/v2/spinner";
import { addSlashes, withCompanyPrefix } from "../route.utils";

// Lazy so the route file doesn't eagerly pull the tabs page (which imports
// nav-tabs.config) during config initialization — avoids a circular-import TDZ.
const Subscription = lazy(() =>
  import("@/pages/(home)/(company)/subscription").then((m) => ({
    default: m.Subscription,
  })),
);
const SubscriptionDashboard = lazy(() =>
  import("@/pages/(home)/(company)/subscription/dashboard").then((m) => ({
    default: m.SubscriptionDashboard,
  })),
);
const SubscriptionManagement = lazy(() =>
  import("@/pages/(home)/(company)/subscription/management").then((m) => ({
    default: m.SubscriptionManagement,
  })),
);
const SubscriptionActivity = lazy(() =>
  import("@/pages/(home)/(company)/subscription/activity").then((m) => ({
    default: m.SubscriptionActivity,
  })),
);
const AiTokenUsage = lazy(() =>
  import("@/pages/(home)/(company)/subscription/ai-token-usage").then((m) => ({
    default: m.AiTokenUsage,
  })),
);
const AiTokenUsageGuidelines = lazy(() =>
  import("@/pages/(home)/(company)/subscription/ai-token-usage-guidlines").then(
    (m) => ({ default: m.AiTokenUsageGuidelines }),
  ),
);

export enum COMPANY_SUBSCRIPTION_PATH_SEGMENTS {
  ROOT = "subscriptions",
  DASHBOARD = "dashboard",
  MANAGEMENT = "management",
  ACTIVITY = "activity",
  AI_TOKEN_USAGE = "ai-token-usage",
  AI_TOKEN_USAGE_GUIDELINES = "ai-token-usage-guidelines",
}

export const companySubscriptionRoutes: RouteObject = {
  path: COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ROOT,
  HydrateFallback: Spinner,
  handle: { breadcrumb: "Subscriptions" },
  children: [
    // ── Page-level tabs layout (outer tabs; inner vTab stays query-param) ────
    {
      Component: Subscription,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_SUBSCRIPTION_PATHS.DASHBOARD),
        },
        {
          path: COMPANY_SUBSCRIPTION_PATH_SEGMENTS.DASHBOARD,
          handle: { breadcrumb: "Dashboard" },
          Component: SubscriptionDashboard,
        },
        {
          path: COMPANY_SUBSCRIPTION_PATH_SEGMENTS.MANAGEMENT,
          handle: { breadcrumb: "Management" },
          Component: SubscriptionManagement,
        },
        {
          path: COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ACTIVITY,
          handle: { breadcrumb: "Activity" },
          Component: SubscriptionActivity,
        },
        {
          path: COMPANY_SUBSCRIPTION_PATH_SEGMENTS.AI_TOKEN_USAGE,
          handle: { breadcrumb: "AI Token Usage" },
          Component: AiTokenUsage,
        },
        {
          path: COMPANY_SUBSCRIPTION_PATH_SEGMENTS.AI_TOKEN_USAGE_GUIDELINES,
          handle: { breadcrumb: "AI Token Usage Guidelines" },
          Component: AiTokenUsageGuidelines,
        },
      ],
    },
  ],
};

const MANAGEMENT_PATH = withCompanyPrefix(
  addSlashes(
    COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ROOT,
    COMPANY_SUBSCRIPTION_PATH_SEGMENTS.MANAGEMENT,
  ),
);

/**
 * Deep link to one of the management page's vertical tabs. The tab is a query
 * param, not a route segment — see `VerticalTabs`, which reads `?vTab=`.
 * Values must match `subscriptionManagementTabsContent` in
 * `@/constants/subscription`.
 */
const _vTab = (vTab: string) => `${MANAGEMENT_PATH}?vTab=${vTab}`;

export const COMPANY_SUBSCRIPTION_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ROOT)),
  DASHBOARD: withCompanyPrefix(
    addSlashes(
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ROOT,
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.DASHBOARD,
    ),
  ),
  MANAGEMENT: MANAGEMENT_PATH,
  VTAB_MANAGEMENT_SEATS: _vTab("seats"),
  ACTIVITY: withCompanyPrefix(
    addSlashes(
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ROOT,
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ACTIVITY,
    ),
  ),
  AI_TOKEN_USAGE: withCompanyPrefix(
    addSlashes(
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ROOT,
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.AI_TOKEN_USAGE,
    ),
  ),
  AI_TOKEN_USAGE_GUIDELINES: withCompanyPrefix(
    addSlashes(
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.ROOT,
      COMPANY_SUBSCRIPTION_PATH_SEGMENTS.AI_TOKEN_USAGE_GUIDELINES,
    ),
  ),
});
